import { Controller } from "@hotwired/stimulus"

interface QueueItem {
  itemId: string
}

// Reflects the global queue onto tracklist rows, container-page style:
// each row carries data-item-id plus play/pause glyphs ([data-role]);
// the current row shows pause while audio plays, play otherwise. Row
// buttons toggle the current track (bubbling to queue-source#playFrom
// starts a different row).
export default class extends Controller {
  static targets = ["row"]
  static outlets = ["playback-queue"]

  declare readonly rowTargets: HTMLElement[]
  declare readonly playbackQueueOutlet: {
    currentItem: QueueItem | undefined
  }

  private currentId: string | null = null
  private audioPlaying = false

  connect() {
    // Rows rendered mid-playback (Turbo navigation, drawer frames) missed
    // the play event — read the player's state snapshot instead.
    this.audioPlaying =
      document.getElementById("persistent-player")?.dataset.playing === "true"
    this.render()
  }

  playbackQueueOutletConnected() {
    this.currentId = this.playbackQueueOutlet.currentItem?.itemId ?? null
    this.render()
  }

  markActive(event: CustomEvent) {
    const detail = event.detail as { item?: QueueItem } | null
    this.currentId = detail?.item?.itemId ?? null
    // Optimistic: a queue change is play intent — show pause on the
    // active row immediately; real pause events correct failures.
    if (event.type === "playback-queue:change") this.audioPlaying = true
    this.render()
  }

  audioStarted() {
    this.audioPlaying = true
    this.render()
  }

  audioStopped() {
    this.audioPlaying = false
    this.render()
  }

  // Row play/pause button: toggles when the row is already current;
  // otherwise falls through to the row's queue-source#playFrom.
  toggle(event: Event) {
    const row = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-item-id]",
    )
    if (row?.dataset.itemId === this.currentId) {
      event.stopPropagation()
      this.dispatch("toggle")
    }
  }

  private render() {
    this.rowTargets.forEach((row) => {
      const current = row.dataset.itemId === this.currentId
      row.dataset.playing = current ? "true" : "false"

      const play = row.querySelector<HTMLElement>("[data-role='row-play']")
      const pause = row.querySelector<HTMLElement>("[data-role='row-pause']")
      const showPause = current && this.audioPlaying
      play?.classList.toggle("hidden", showPause)
      pause?.classList.toggle("hidden", !showPause)
    })
  }
}
